import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DateRangePicker from '@mui/lab/DateRangePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import { fetchNotificationTotalIntegratedPlatform } from './notificationsSlice';

// Reusable standard layout styling configurations dictionary for production form controls
const enterpriseNotificationFilterStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE BLUE BRAND ACCENT MATRIX ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-root': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 9px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& .MuiSelect-select, & input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const Filters = ({ visible, meta, setMeta }) => {
    const dispatch = useDispatch();
    const report = useSelector((state) => state.notificationdashboard.report);  
    const totalintegratedplatforms = useSelector((state) => state.notificationdashboard.totalintegratedplatforms);  
    const categoriescount = useSelector((state) => state.notificationdashboard.categoriescount);
    const [formValues, setFormValues] = useState(meta)
    const [filterButtonDisabled, setFilterButtonDisabled] = useState(true)
    const [value, setValue] = React.useState([null, null]);
    const [checkFormUse, setCheckFormUse] = useState("")
    
    const onInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value,
            [`page`]: 1
        });
    };

    const submitFilterValues = () => {
        setMeta(prevState => ({
            ...prevState,
            [`platform`]: formValues["platform"],
            [`event`]: formValues["event"],
            [`date`]: formValues["date"],
            [`page`]: 1
        }));
        setCheckFormUse(true)
    }

    const onDateChange = (date) => {
        setValue(date)
        let start_date = ""
        let end_date = ""
        if (date[0] != null) {
            start_date = moment(date[0]).format('YYYY-MM-DD')
        }
        if (date[1] != null) {
            end_date = moment(date[1]).format('YYYY-MM-DD')
        }
        setFormValues({
            ...formValues,
            [`date`]: (start_date !== "" && end_date !== "") ? start_date + "," + end_date : ""
        });
    }

    const resetFields = () => {
        setFormValues({
            ...formValues,
            [`platform`]: "",
            [`event`]: "",
            [`date`]: "",
        });
        setMeta(prevState => ({
            ...prevState,
            [`platform`]: "",
            [`event`]: "",
            [`date`]: "",
            [`page`]: 1
        }));
        setValue([null, null])
    }

    const checkFormHasValues = () => {
        let disableButton = true
        if (formValues["date"] !== "" || formValues["platform"] !== "" || formValues["event"] !== "") {
            disableButton = false
        }

        return disableButton
    }

    useEffect(() => {
        setCheckFormUse("")
        setFilterButtonDisabled(checkFormHasValues())
    }, [formValues])

    useEffect(() => {
        if ((formValues["date"] !== "" || formValues["platform"] !== "") && checkFormUse === '') {
            setFormValues({
                ...formValues,
                [`platform`]: "",
                [`event`]:"",
                [`date`]: "",
            });
            setMeta(prevState => ({
                ...prevState,
                [`platform`]: "",
                [`event`]: "",
                [`date`]: "",
                [`page`]: 1
            }));
            setValue([null, null])
        }
    }, [report])
    
    useEffect(() => {
        dispatch(fetchNotificationTotalIntegratedPlatform());
    }, []);

    return (
        <>
            {visible &&
                <Box 
                    className="filtercontainer"
                    sx={{
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        p: 3,
                        mb: 3,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box
                        className="filteraccordion"
                        id="sc-filteraccordion"
                        component="form"
                        noValidate
                        autoComplete="off"
                    >
                        <Grid container spacing={2.5}>
                            
                            {/* 1. Integration Platform Multi-Option Dropdown Menu Selector Module */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <FormControl variant="outlined" size="small" sx={enterpriseNotificationFilterStyles}>
                                        <InputLabel id="carrier-name-select-label">Platform</InputLabel>
                                        <Select                                    
                                            value={formValues && formValues.platform}
                                            name={"platform"}
                                            labelId="carrier-name-select-label"
                                            id="demo-simple-select"
                                            label="Platform"
                                            onChange={onInputChange}
                                        >
                                            {totalintegratedplatforms.data &&
                                             totalintegratedplatforms.data.platforms &&
                                             totalintegratedplatforms.data.platforms.map((item, text) => {
                                                return <MenuItem value={item[0]} key={item[0]}>{item[0]}</MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>

                            {/* 2. System Log Categories Inbound Events Dropdown Selector Module */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <FormControl variant="outlined" size="small" sx={enterpriseNotificationFilterStyles}>
                                        <InputLabel id="order-status-select-label">Events</InputLabel>
                                        <Select                                    
                                            value={formValues && formValues.event}
                                            name={"event"} 
                                            labelId="order-status-select-label"
                                            id="demo-simple-select"
                                            label="Events"
                                            onChange={onInputChange}
                                        >
                                            {categoriescount.data &&
                                             categoriescount.data.categories_count &&
                                             categoriescount.data.categories_count.map((item, text) => {
                                                return <MenuItem value={item.category} key={item.category}>{item.category}</MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>

                            {/* 3. Date Timeline Range Core Frame Selector Picker Module */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ minHeight: '44px' }}>
                                    <FormControl variant="outlined" size="small" sx={{ width: '100%' }}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DateRangePicker
                                                startText="Start Date"
                                                endText="End Date"
                                                disableFuture
                                                value={value}
                                                views={["year", "month", "day"]}
                                                clearable={true}
                                                format={"yyyy-MM-dd"}
                                                onChange={(newValue) => {
                                                    onDateChange(newValue);
                                                }}
                                                renderInput={(startProps, endProps) => (
                                                    <Grid container spacing={1.5} alignItems="center">
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                variant="outlined"
                                                                fullWidth={true}
                                                                label="From"
                                                                size={'small'}
                                                                {...startProps}
                                                                InputProps={{
                                                                    ...startProps.InputProps,
                                                                    endAdornment: (value[0] != null) ? (
                                                                        <IconButton size="small" onClick={() => onDateChange([null, null])} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                                            <ClearIcon fontSize="small" />
                                                                        </IconButton>
                                                                    ) : startProps.InputProps?.endAdornment
                                                                }}
                                                                sx={enterpriseNotificationFilterStyles}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                variant="outlined"
                                                                fullWidth={true}
                                                                label="End"
                                                                size={'small'}
                                                                {...endProps}
                                                                InputProps={{
                                                                    ...endProps.InputProps,
                                                                    endAdornment: (value[1] != null) ? (
                                                                        <IconButton size="small" onClick={() => onDateChange([null, null])} sx={{ color: '#94A3B8', marginRight: '-4px' }}>
                                                                            <ClearIcon fontSize="small" />
                                                                        </IconButton>
                                                                    ) : endProps.InputProps?.endAdornment
                                                                }}
                                                                sx={enterpriseNotificationFilterStyles}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Box>
                            </Grid>

                            {/* 4. Action Confirmation Terminal Execution Elements Lower Control Bar */}
                            <Grid item xs={12}>
                                <Box 
                                    className="filterfooter"
                                    sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'flex-end', 
                                        gap: 1.5,
                                        pt: 1,
                                        borderTop: '1px solid #E2E8F0'
                                    }}
                                >
                                    <Button 
                                        variant="outlined" 
                                        onClick={resetFields} 
                                        disabled={filterButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            px: 3,
                                            height: '38px',
                                            borderColor: '#E2E8F0',
                                            color: '#475569',
                                            '&:hover': {
                                                borderColor: '#CBD5E1',
                                                backgroundColor: '#F1F5F9',
                                                color: '#1E293B'
                                            },
                                            '&.Mui-disabled': {
                                                borderColor: '#E2E8F0',
                                                color: '#CBD5E1'
                                            }
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        onClick={submitFilterValues} 
                                        disabled={filterButtonDisabled}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '13.5px',
                                            px: 4,
                                            height: '38px',
                                            boxShadow: 'none',
                                            backgroundColor: '#1976d2',
                                            color: '#FFFFFF',
                                            '&:hover': { 
                                                backgroundColor: '#115293',
                                                boxShadow: 'none' 
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: '#E2E8F0',
                                                color: '#94A3B8'
                                            }
                                        }}
                                    >
                                        Filter
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>
                </Box>
            }
        </>
    );
};

export default Filters;